import React, { useCallback, useEffect, useState } from 'react';
import { ArrowRight, Plus } from 'lucide-react';
import { TaskyAlert, TaskyButton, TaskyCard, TaskySpinner } from '../tasky';
import easyVideoService, {
  type EasyVideoProject,
  type EasyVideoProjectSummary,
} from '../../services/api/easyVideoService';
import EasyVideoWizard, { type EasyVideoWizardStep } from './EasyVideoWizard';
import EasyVideoProgress from './EasyVideoProgress';
import EasyVideoResult from './EasyVideoResult';

export interface EasyVideoTabProps {
  /** URL の ?project=<uuid>。null なら新規作成から */
  projectId: string | null;
  onProjectIdChange: (projectId: string | null) => void;
  /** ログイン中のユーザー ID（自分の下書きだけを片付けるのに使う） */
  currentUserId: number | null;
}

const STATUS_LABEL: Record<EasyVideoProjectSummary['status'], string> = {
  draft: '質問に回答中',
  running: '作成中',
  completed: '完成',
  failed: '止まっています',
};

const EasyVideoTab: React.FC<EasyVideoTabProps> = ({ projectId, onProjectIdChange, currentUserId }) => {
  const [project, setProject] = useState<EasyVideoProject | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [inProgress, setInProgress] = useState<EasyVideoProjectSummary[]>([]);
  const [wizardKey, setWizardKey] = useState(0);
  const [wizardStep, setWizardStep] = useState<EasyVideoWizardStep | undefined>(undefined);
  const [showScenes, setShowScenes] = useState(false);
  const [anotherLoading, setAnotherLoading] = useState(false);
  const [anotherError, setAnotherError] = useState<string | null>(null);

  // URL の project が変わったら読み直す（ウィザードが作った下書きは手元にあるので読み直さない）
  useEffect(() => {
    if (!projectId) {
      setProject(null);
      setLoadError(null);
      return undefined;
    }
    if (project?.id === projectId) return undefined;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    easyVideoService
      .get(projectId)
      .then((next) => {
        if (cancelled) return;
        setProject(next);
        setWizardStep(undefined);
        setWizardKey((k) => k + 1);
        setShowScenes(false);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(easyVideoService.extractErrorMessage(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // project は「手元の下書きと同じか」の照合にだけ使う
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // 作りかけ一覧（新規開始の画面でだけ出す）
  useEffect(() => {
    if (projectId) return undefined;
    let cancelled = false;
    easyVideoService
      .list()
      .then((items) => {
        if (!cancelled) setInProgress(items.filter((item) => item.status !== 'completed'));
      })
      .catch(() => {
        if (!cancelled) setInProgress([]);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const handleProjectCreated = useCallback(
    (created: EasyVideoProject) => {
      setProject(created);
      onProjectIdChange(created.id);
    },
    [onProjectIdChange]
  );

  const handleStarted = useCallback((started: EasyVideoProject) => {
    setProject(started);
  }, []);

  const handleUpdate = useCallback((next: EasyVideoProject) => {
    setProject((prev) => {
      // 完成後に作り直して再び完成したら、結果画面に戻す
      if (prev && prev.status !== 'completed' && next.status === 'completed') setShowScenes(false);
      return next;
    });
  }, []);

  const handleStartNew = () => {
    setProject(null);
    setWizardStep(undefined);
    setWizardKey((k) => k + 1);
    onProjectIdChange(null);
  };

  const handleAnotherOne = async () => {
    if (!project?.product) return;
    setAnotherLoading(true);
    setAnotherError(null);
    try {
      const created = await easyVideoService.create(project.product.id);
      const withAspect =
        created.aspect_ratio === project.aspect_ratio
          ? created
          : await easyVideoService.update(created.id, { aspect_ratio: project.aspect_ratio });
      setProject(withAspect);
      setWizardStep(3);
      setWizardKey((k) => k + 1);
      setShowScenes(false);
      onProjectIdChange(withAspect.id);
    } catch (e) {
      setAnotherError(easyVideoService.extractErrorMessage(e));
    } finally {
      setAnotherLoading(false);
    }
  };

  const renderInProgress = () => (
    <TaskyCard variant="warm" padding="sm" className="space-y-2" data-testid="easy-video-in-progress">
      <p className="text-sm font-semibold text-dark">作成中の動画があります</p>
      <ul className="space-y-2">
        {inProgress.slice(0, 5).map((item) => (
          <li key={item.id} className="flex flex-wrap items-center justify-between gap-2">
            <span className="min-w-0 text-sm text-gray-700">
              <span className="font-medium">{item.product?.name ?? '商品未選択'}</span>
              <span className="ml-2 text-gray-500">
                {STATUS_LABEL[item.status]}
                {item.created_by?.name && `・${item.created_by.name}さん`}
              </span>
            </span>
            <TaskyButton size="sm" variant="secondary" onClick={() => onProjectIdChange(item.id)}>
              続きを見る
              <ArrowRight />
            </TaskyButton>
          </li>
        ))}
      </ul>
    </TaskyCard>
  );

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center py-16">
          <TaskySpinner />
        </div>
      );
    }
    if (loadError) {
      return (
        <div className="mx-auto max-w-2xl space-y-3 px-4 py-4">
          <TaskyAlert tone="error">{loadError}</TaskyAlert>
          <TaskyButton variant="secondary" onClick={handleStartNew}>
            新しく作る
          </TaskyButton>
        </div>
      );
    }
    if (!project || project.status === 'draft') {
      return (
        <EasyVideoWizard
          key={wizardKey}
          project={project}
          initialStep={wizardStep}
          currentUserId={currentUserId}
          onProjectCreated={handleProjectCreated}
          onStarted={handleStarted}
        />
      );
    }
    if (project.status === 'completed' && !showScenes) {
      return (
        <>
          <EasyVideoResult
            project={project}
            onAnotherOne={handleAnotherOne}
            anotherOneLoading={anotherLoading}
            error={anotherError}
          />
          <div className="mx-auto max-w-2xl px-4 pb-6">
            <TaskyButton variant="link" size="sm" onClick={() => setShowScenes(true)}>
              シーンを見る・作り直す
            </TaskyButton>
          </div>
        </>
      );
    }
    return <EasyVideoProgress project={project} onUpdate={handleUpdate} onBack={handleStartNew} />;
  };

  const showNewButton = !!project && project.status !== 'draft';

  return (
    <div className="flex flex-col">
      {!projectId && inProgress.length > 0 && <div className="mx-auto w-full max-w-2xl px-4 pt-4">{renderInProgress()}</div>}
      {showNewButton && (
        <div className="mx-auto flex w-full max-w-4xl justify-end px-4 pt-4">
          <TaskyButton size="sm" variant="ghost" onClick={handleStartNew}>
            <Plus />
            新しく作る
          </TaskyButton>
        </div>
      )}
      {renderContent()}
    </div>
  );
};

export default EasyVideoTab;
